// Feature #286 — the live 1-on-1 review view.
//
// Feature #340 rebuilt this screen around the SKILL rather than the delta.
// A skill used to carry one proposal, so a flat list of deltas read fine.
// With requested peer assessments (#326) it routinely carries three or
// four — a self assessment, a colleague's independent view, the manager's
// own — from people who have never spoken to each other, and a flat list
// made the manager reassemble the argument themselves. Worse, resolving
// them one at a time was never coherent: each resolution wrote the current
// rating, so the last one clicked won, and any left unresolved detached
// back to the open queue at close and reappeared as though the meeting had
// not happened.
//
// So each skill is one card showing every competing rating with its reason,
// and one agreed number settles all of them (R5 provenance write-back).
//
// The SUBJECT can read this page too, but since #364 not the same payload:
// people do not see peer assessments made about them, so the API sends the
// subject only the proposals they made themselves, plus the per-skill
// outcome (the rating on the profile now, the agreed rating once there is
// one). A card with no proposal on it is a skill somebody ELSE raised —
// not an empty decision — and the copy below says so rather than counting
// "0 proposed ratings". `read_only` comes from the API and only decides
// whether the controls render; the write endpoints are sm_manager-gated
// regardless.
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDateTime } from '@/utils/dateFormat'
import {
  closeReview,
  getReview,
  getRubric,
  resolveSkill,
  SmReview,
  SmReviewSkillGroup,
  SmRubricLevel,
} from '@/api/sm'

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const ratingBox: React.CSSProperties = {
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  padding: '8px 10px',
}

const ratingBoxLabel: React.CSSProperties = {
  fontSize: '11px',
  textTransform: 'uppercase',
  color: 'var(--color-dark-grey)',
  marginBottom: '2px',
}

const ratingDesc: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-dark-grey)',
  lineHeight: 1.5,
  marginTop: '2px',
}

const ReviewDetail: React.FC = () => {
  const navigate = useNavigate()
  const { reviewId } = useParams<{ reviewId: string }>()
  const [review, setReview] = useState<SmReview | null>(null)
  // Keyed by SKILL id, not delta id — the unit of decision is the skill.
  const [agreed, setAgreed] = useState<Record<string, number>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [meetingNotes, setMeetingNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busySkill, setBusySkill] = useState<string | null>(null)
  // Feature #306: per-type rubric so each rating can show its descriptor.
  const [rubric, setRubric] = useState<SmRubricLevel[]>([])

  const load = () => {
    if (!reviewId) return
    getReview(reviewId)
      .then(setReview)
      .catch((e) =>
        setError(e?.response?.data?.detail || e?.message || 'Failed to load review')
      )
  }

  useEffect(load, [reviewId])

  useEffect(() => {
    getRubric()
      .then(setRubric)
      .catch(() => setRubric([]))
  }, [])

  // Rubric level for a given skill type + rating value (Feature #306). Only
  // the technical set arrives since #355; keyed by type regardless, because
  // that is still what the lookup means.
  const levelFor = (skillType?: string | null, value?: number | null) =>
    value ? rubric.find((r) => r.skill_type === skillType && r.value === value) : undefined
  const levelsFor = (skillType?: string | null) =>
    rubric.filter((r) => r.skill_type === skillType)

  const handleResolve = async (group: SmReviewSkillGroup) => {
    if (!reviewId || !agreed[group.skill_id]) return
    setError(null)
    setBusySkill(group.skill_id)
    try {
      // Returns the whole review: settling one skill changes the subject's
      // current rating, which every other card shows as its baseline.
      const updated = await resolveSkill(
        reviewId,
        group.skill_id,
        agreed[group.skill_id],
        notes[group.skill_id] || undefined
      )
      setReview(updated)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to record the rating')
    } finally {
      setBusySkill(null)
    }
  }

  const handleClose = async () => {
    if (!reviewId) return
    setError(null)
    try {
      await closeReview(reviewId, meetingNotes || undefined)
      navigate('/sm/reviews')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to close review')
    }
  }

  const groups = review?.skill_groups || []
  const unresolvedGroups = groups.filter((g) => !g.resolved)
  const canEdit = Boolean(review && !review.read_only && review.status === 'in_review')

  const renderGroup = (group: SmReviewSkillGroup) => {
    const options = levelsFor(group.skill_type)
    const currentLevel = levelFor(group.skill_type, group.current_rating)
    const pendingLevel = levelFor(group.skill_type, agreed[group.skill_id])
    const resolvedLevel = levelFor(group.skill_type, group.agreed_rating)

    return (
      <div
        key={group.skill_id}
        style={{
          border: '1px solid var(--color-light-grey)',
          borderLeft: group.resolved
            ? '4px solid #16a34a'
            : '4px solid var(--color-magenta)',
          borderRadius: '8px',
          padding: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '10px',
            marginBottom: '12px',
            flexWrap: 'wrap',
          }}
        >
          <strong style={{ fontSize: '15px' }}>{group.skill_name}</strong>
          <span style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
            {review?.read_only
              ? // #364 — the subject's copy holds only their own proposals.
                group.deltas.length === 0
                ? 'raised by someone else — your own proposals appear here'
                : group.deltas.length === 1
                ? 'your proposed rating'
                : `your ${group.deltas.length} proposed ratings`
              : group.deltas.length === 1
              ? '1 proposed rating'
              : `${group.deltas.length} proposed ratings to reconcile`}
          </span>
        </div>

        <div style={{ ...ratingBox, marginBottom: '12px' }}>
          <div style={ratingBoxLabel}>Rating on the profile now</div>
          <div style={{ fontWeight: 500 }}>
            {group.current_rating
              ? `${group.current_rating}${currentLevel ? ` · ${currentLevel.label}` : ''}`
              : 'Not rated'}
          </div>
          {currentLevel?.description && (
            <div style={ratingDesc}>{currentLevel.description}</div>
          )}
        </div>

        {/* #340 — every rank and its reason, in the order the opinions
            arrived. This is the list the subject reads too. */}
        <div style={{ display: 'grid', gap: '8px', marginBottom: '14px' }}>
          {group.deltas.map((d) => {
            const level = levelFor(d.skill_type, d.proposed_rating)
            return (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '10px 12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '15px',
                    minWidth: '22px',
                    textAlign: 'center',
                  }}
                >
                  {d.proposed_rating}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500 }}>
                    {level ? level.label : `Rated ${d.proposed_rating}`}
                  </span>
                  <span style={{ color: 'var(--color-dark-grey)' }}>
                    {' — '}
                    {d.proposed_by_display_name || 'unknown'} on{' '}
                    {formatDateTime(d.proposed_at)}
                  </span>
                  {level?.description && (
                    <span style={{ ...ratingDesc, display: 'block' }}>
                      {level.description}
                    </span>
                  )}
                  {/* #351 — the proposer's reason. This is what the
                      review is for: the number says what they think,
                      this says why, and three of these side by side is
                      the conversation. */}
                  {d.comment ? (
                    <span
                      style={{
                        display: 'block',
                        marginTop: '6px',
                        fontSize: '13px',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      “{d.comment}”
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'block',
                        marginTop: '6px',
                        fontSize: '12px',
                        color: 'var(--color-dark-grey)',
                      }}
                    >
                      No reason given.
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>

        {group.resolved ? (
          <div style={{ fontSize: '14px' }}>
            Agreed rating:{' '}
            <strong>
              {group.agreed_rating}
              {resolvedLevel ? ` · ${resolvedLevel.label}` : ''}
            </strong>
            {resolvedLevel?.description && (
              <div style={ratingDesc}>{resolvedLevel.description}</div>
            )}
            {group.delta_notes && (
              <span style={{ color: 'var(--color-dark-grey)' }}> — {group.delta_notes}</span>
            )}
          </div>
        ) : canEdit ? (
          <>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <select
                aria-label={`Agreed rating for ${group.skill_name}`}
                style={inputStyle}
                value={agreed[group.skill_id] || ''}
                onChange={(e) =>
                  setAgreed((prev) => ({
                    ...prev,
                    [group.skill_id]: Number(e.target.value),
                  }))
                }
              >
                <option value="">Agreed rating…</option>
                {(options.length
                  ? options.map((r) => ({ value: r.value, label: r.label }))
                  : [1, 2, 3, 4, 5].map((v) => ({ value: v, label: '' }))
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.value}
                    {o.label ? ` · ${o.label}` : ''}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Notes (optional)"
                style={{ ...inputStyle, flex: 1, minWidth: '200px' }}
                value={notes[group.skill_id] || ''}
                onChange={(e) =>
                  setNotes((prev) => ({ ...prev, [group.skill_id]: e.target.value }))
                }
              />
              <button
                onClick={() => handleResolve(group)}
                disabled={!agreed[group.skill_id] || busySkill === group.skill_id}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: agreed[group.skill_id] ? 'pointer' : 'not-allowed',
                  opacity: agreed[group.skill_id] ? 1 : 0.5,
                  fontFamily: 'var(--font-family)',
                }}
              >
                {busySkill === group.skill_id ? 'Saving…' : 'Record agreed rating'}
              </button>
            </div>
            {pendingLevel?.description && (
              <div style={{ ...ratingDesc, marginTop: '8px' }}>
                {pendingLevel.description}
              </div>
            )}
            {group.deltas.length > 1 && (
              <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', margin: '8px 0 0' }}>
                One agreed rating settles all {group.deltas.length} proposals on this
                skill.
              </p>
            )}
          </>
        ) : (
          <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: 0 }}>
            Awaiting an agreed rating.
          </p>
        )}
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title={
          review ? `Review — ${review.user_display_name || review.user_id}` : 'Review'
        }
      />
      <div style={{ padding: '24px', display: 'grid', gap: '24px', maxWidth: '1000px' }}>
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
        {!review && !error && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
        {review && (
          <>
            <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: 0 }}>
              Opened {formatDateTime(review.opened_at)} — status:{' '}
              <strong>{review.status.replace('_', ' ')}</strong>
              {review.read_only && ' · your manager records the agreed ratings'}
            </p>
            {review.read_only && (
              <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: 0 }}>
                Only the ratings you proposed yourself are shown. Each skill still shows the
                rating on your profile now and, once your manager settles it, the agreed rating.
              </p>
            )}

            <div style={{ display: 'grid', gap: '16px' }}>{groups.map(renderGroup)}</div>

            {canEdit && (
              <div
                style={{
                  borderTop: '1px solid var(--color-light-grey)',
                  paddingTop: '16px',
                  display: 'grid',
                  gap: '12px',
                }}
              >
                <textarea
                  placeholder="Meeting notes (optional)"
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
                />
                {unresolvedGroups.length > 0 && (
                  <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                    {unresolvedGroups.length} skill(s) still have no agreed rating.
                    Their proposals will detach back to the open queue when you close
                    this review.
                  </p>
                )}
                <div>
                  <button
                    onClick={handleClose}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: 'var(--color-black)',
                      color: 'var(--color-white)',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Close review
                  </button>
                </div>
              </div>
            )}

            {review.status === 'resolved' && review.meeting_notes && (
              <div
                style={{
                  borderTop: '1px solid var(--color-light-grey)',
                  paddingTop: '16px',
                  fontSize: '14px',
                }}
              >
                <div style={ratingBoxLabel}>Meeting notes</div>
                {review.meeting_notes}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ReviewDetail
